/**
 * DeductionRiderToSupplierPage Component
 * 라이더 → 협력사 일차감 페이지
 *
 * 협력사가 라이더에게 부과하는 일차감 관리
 * - 조회: DEDUCT_TARGET='R' AND DEDUCT_REG_TYPE='S'
 * - 등록: DEDUCT_TARGET='R', DEDUCT_REG_TYPE='S'
 * - 본사 사용자: 협력사 선택 후 일차감 조회
 * - 협력사 사용자: 바로 자신의 라이더 일차감 관리
 *
 * 테이블 컬럼: No, 대상(라이더), 공제타입, 공제명, 공제금액, 기간
 */
import React, { useState, useEffect, useCallback } from "react";
import { getItem } from '@/shared/utils/storage';
import { ArrowLeftIcon } from '@/shared/components/icons/Icons';
import SupplierSelectionPage from '@/features/partner/supplierStatus/components/SupplierSelectionPage';
import Loading from '@/shared/components/feedback/Loading';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import { deductionApi, DeductionDto, riderApi, RiderInfoDto } from "@/shared/api/deductionApi";
import { usePagination } from "@/shared/hooks/usePagination";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { formatCurrency } from "@/shared/utils/formatter";
import DateInput from "@/shared/components/forms/DateInput";
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

/** 공제타입 옵션 */
const DEDUCTION_TYPE_OPTIONS = [
    { code: 'DAILY', name: '일차감' },
    { code: 'WEEKLY', name: '주차감' }
];

/** 새 일차감 등록 폼 데이터 타입 */
interface NewDeductionForm {
    riderId: string;
    deductionTypeCode: string;
    deductionName: string;
    deductionAmount: string;
    startDate: string;
    endDate: string;
}

const DeductionRiderToSupplierPage: React.FC = () => {
    // ========================================
    // 1. State - 사용자 정보 및 협력사 선택
    // ========================================
    const [userType, setUserType] = useState<string>('');
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
    const [selectedSupplierName, setSelectedSupplierName] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // ========================================
    // 2. Side Effects - 초기 데이터 로드
    // ========================================
    useEffect(() => {
        const userTypeValue = getItem<string>('USER_TYPE');
        const userId = getItem<string>('USER_ID');
        setUserType(userTypeValue || '');

        // 협력사 사용자는 자동으로 자신의 ID 설정
        if (userTypeValue === 'SUPPLIER' && userId) {
            setSelectedSupplierId(userId);
        }

        setIsInitialized(true);
    }, []);

    // ========================================
    // 3. Event Handlers - 협력사 선택
    // ========================================
    const handleSupplierSelect = useCallback((supplierId: string, supplierName: string) => {
        setSelectedSupplierId(supplierId);
        setSelectedSupplierName(supplierName);
    }, []);

    const handleBackToSelection = useCallback(() => {
        setSelectedSupplierId('');
        setSelectedSupplierName('');
    }, []);

    // ========================================
    // 4. Render - 초기화 대기
    // ========================================
    if (!isInitialized) {
        return <Loading message="사용자 정보를 확인하는 중..." />;
    }

    // ========================================
    // 5. Render - 본사 사용자 협력사 선택 페이지
    // ========================================
    if (userType === 'HEADQUARTERS' && !selectedSupplierId) {
        return (
            <SupplierSelectionPage
                onSupplierSelect={handleSupplierSelect}
                pageTitle="라이더 → 협력사 일차감"
            />
        );
    }

    // ========================================
    // 6. Render - 일차감 관리 페이지
    // ========================================
    return (
        <RiderToSupplierDeductionContent
            supplierId={selectedSupplierId}
            supplierName={selectedSupplierName}
            userType={userType}
            onBack={handleBackToSelection}
        />
    );
};

// ========================================
// 일차감 관리 컨텐츠 컴포넌트
// ========================================
interface RiderToSupplierDeductionContentProps {
    supplierId: string;
    supplierName: string;
    userType: string;
    onBack: () => void;
}

const RiderToSupplierDeductionContent: React.FC<RiderToSupplierDeductionContentProps> = ({
    supplierId,
    supplierName,
    userType,
    onBack
}) => {
    const { showToast } = useToast();

    // 로딩 상태
    const [loading, setLoading] = useState(false);
    const [ridersLoading, setRidersLoading] = useState(false);

    // 데이터
    const [deductions, setDeductions] = useState<DeductionDto[]>([]);
    const [riders, setRiders] = useState<RiderInfoDto[]>([]);

    // 필터
    const [filterRiderId, setFilterRiderId] = useState<string>('');

    // 새 일차감 등록 폼
    const [newDeduction, setNewDeduction] = useState<NewDeductionForm>({
        riderId: '',
        deductionTypeCode: '',
        deductionName: '',
        deductionAmount: '',
        startDate: '',
        endDate: ''
    });

    // 페이지네이션
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: deductions, itemsPerPage: ITEMS_PER_PAGE });

    /**
     * 라이더 목록 조회
     */
    const fetchRiders = useCallback(async () => {
        if (!supplierId) return;
        try {
            setRidersLoading(true);
            const response = await riderApi.getRiderList(supplierId);
            setRiders(response.riders || []);
        } catch {
            showToast('라이더 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setRiders([]);
        } finally {
            setRidersLoading(false);
        }
    }, [supplierId, showToast]);

    /**
     * 일차감 목록 조회
     * 조건: DEDUCT_TARGET='R' AND DEDUCT_REG_TYPE='S'
     */
    const fetchDeductions = useCallback(async (riderId?: string) => {
        if (!supplierId) return;
        try {
            setLoading(true);

            const response = await deductionApi.getDeductionList({
                suppId: supplierId,
                deductTarget: 'R'
            });

            // 클라이언트 측 필터링: deductTarget='R' AND deductRegType='S'
            let filteredDeductions = (response.deducts || []).filter(d =>
                d.deductTarget === 'R' && d.deductRegType === 'S'
            );

            // 라이더 필터 적용
            if (riderId) {
                filteredDeductions = filteredDeductions.filter(d => d.riderId === riderId);
            }

            // seqNo 기준 내림차순 정렬 (최신순)
            filteredDeductions.sort((a, b) => b.seqNo - a.seqNo);

            setDeductions(filteredDeductions);
        } catch {
            showToast('일차감 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setDeductions([]);
        } finally {
            setLoading(false);
        }
    }, [supplierId, showToast]);

    /**
     * 조회 버튼 클릭
     */
    const handleSearch = () => {
        fetchDeductions(filterRiderId);
        resetPagination();
    };

    /**
     * 초기 데이터 로드
     */
    useEffect(() => {
        fetchRiders();
        fetchDeductions();
    }, [fetchRiders, fetchDeductions]);

    /**
     * 새 일차감 등록
     */
    const handleAddDeduction = async () => {
        // 유효성 검사
        if (!newDeduction.riderId) {
            showToast('라이더를 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionTypeCode) {
            showToast('공제타입을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionName) {
            showToast('공제명을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.deductionAmount) {
            showToast('공제금액을 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.startDate) {
            showToast('시작일자를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }
        if (!newDeduction.endDate) {
            showToast('종료일자를 입력해주세요.', { type: ToastType.WARNING });
            return;
        }

        try {
            const userId = getItem<string>('USER_ID') || '';
            const response = await deductionApi.registerDeduction({
                deductTarget: 'R',
                deductRegType: 'S',
                suppId: supplierId,
                riderId: newDeduction.riderId,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1', // 정액
                deductName: newDeduction.deductionName,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insUser: userId
            });

            showToast('일차감이 등록되었습니다.', { type: ToastType.SUCCESS });

            // 낙관적 업데이트
            const newDeductionData: DeductionDto = {
                seqNo: response.seqNo || Date.now(),
                deductTarget: 'R',
                deductRegType: 'S',
                suppId: supplierId,
                riderId: newDeduction.riderId,
                deductType: newDeduction.deductionTypeCode,
                calcType: '1',
                deductName: newDeduction.deductionName,
                deductCnt: null,
                deductAmt: parseInt(newDeduction.deductionAmount, 10),
                deductRate: null,
                deductDispYn: 'Y',
                startDay: newDeduction.startDate,
                endDay: newDeduction.endDate,
                insDate: new Date().toISOString(),
                insUser: userId,
                updDate: null,
                updUser: null
            };

            setDeductions(prev => [newDeductionData, ...prev]);

            // 폼 초기화
            setNewDeduction({
                riderId: '',
                deductionTypeCode: '',
                deductionName: '',
                deductionAmount: '',
                startDate: '',
                endDate: ''
            });

            resetPagination();
        } catch {
            showToast('일차감 등록에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    /**
     * 라이더 옵션 변환
     * 형식: 라이더이름(라이더아이디)
     */
    const riderOptions: SelectOption[] = riders.map((rider) => ({
        value: rider.riderId,
        label: rider.riderName ? `${rider.riderName}(${rider.riderId})` : rider.riderId
    }));

    /**
     * 라이더명 조회
     */
    const getRiderLabel = (riderId: string): string => {
        const rider = riders.find(r => r.riderId === riderId);
        if (rider) {
            return rider.riderName ? `${rider.riderName}(${rider.riderId})` : rider.riderId;
        }
        return riderId;
    };

    /**
     * 공제타입명 조회
     */
    const getDeductionTypeName = (code: string): string => {
        const type = DEDUCTION_TYPE_OPTIONS.find(t => t.code === code);
        return type ? type.name : code;
    };

    /**
     * 기간 포맷
     */
    const formatPeriod = (startDay: string, endDay: string): string => {
        if (!startDay && !endDay) return '-';
        if (!endDay) return `${startDay} ~`;
        if (!startDay) return `~ ${endDay}`;
        return `${startDay} ~ ${endDay}`;
    };

    // 로딩 중
    if (loading && deductions.length === 0) {
        return (
            <div className="supplier-selection-page">
                <div className="page-title-wrapper">
                    <h1 className="page-title">라이더 → 협력사 일차감</h1>
                </div>
                <Loading message="일차감 내역을 불러오는 중..." />
            </div>
        );
    }

    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">
                    라이더 → 협력사 일차감
                    {userType === 'HEADQUARTERS' && supplierName && (
                        <span style={{
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: 'var(--vroong-green)',
                            marginLeft: '12px',
                        }}>
                            {supplierName}
                        </span>
                    )}
                </h1>
                {/* 본사 사용자용 뒤로가기 버튼 */}
                {userType === 'HEADQUARTERS' && (
                    <button
                        className="search-btn"
                        onClick={onBack}
                        style={{ marginLeft: 'auto' }}
                    >
                        <ArrowLeftIcon width={16} height={16} />
                        <span>협력사 목록으로</span>
                    </button>
                )}
            </div>

            {/* 필터 섹션 */}
            <div className="card search-card">
                <div className="card-body">
                    <div className="search-form rider-to-supplier-search-form">
                        {/* 라이더 필터 */}
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-motorcycle" />
                                    라이더
                                </label>
                                <SearchableSelect
                                    options={riderOptions}
                                    value={filterRiderId}
                                    onChange={setFilterRiderId}
                                    placeholder={ridersLoading ? '로딩 중...' : '라이더 선택'}
                                    disabled={ridersLoading}
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>

                        {/* 조회 버튼 */}
                        <div className="form-group button-group">
                            <button
                                className="btn btn-primary"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                <i className="fas fa-search" />
                                조회
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 테이블 섹션 */}
            <div className="table-container" style={{ overflowX: 'auto' }}>
                <table className="supplier-selection-table" style={{ minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '60px' }}>NO</th>
                            <th style={{ width: '250px' }}>대상</th>
                            <th style={{ width: '120px' }}>공제타입</th>
                            <th style={{ width: '200px' }}>공제명</th>
                            <th style={{ width: '120px' }}>공제금액</th>
                            <th style={{ width: '200px' }}>기간</th>
                            <th style={{ width: '80px' }}>등록</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* 기존 데이터 행 */}
                        {currentItems.length === 0 && !loading ? (
                            <tr>
                                <td colSpan={7} className="text-center text-gray-600">
                                    조회된 일차감 내역이 없습니다.
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((deduction, index) => (
                                <tr key={deduction.seqNo}>
                                    <td className="text-center">
                                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                    </td>
                                    <td>{getRiderLabel(deduction.riderId)}</td>
                                    <td className="text-center">{getDeductionTypeName(deduction.deductType)}</td>
                                    <td>{deduction.deductName}</td>
                                    <td className="text-right">{formatCurrency(String(deduction.deductAmt || 0))}원</td>
                                    <td className="text-center">{formatPeriod(deduction.startDay, deduction.endDay)}</td>
                                    <td></td>
                                </tr>
                            ))
                        )}

                        {/* 새 일차감 등록 행 */}
                        <tr className="add-row" style={{ backgroundColor: '#f8f9fa' }}>
                            <td className="text-center text-gray-600">+</td>
                            {/* 대상: 라이더 선택 */}
                            <td>
                                <SearchableSelect
                                    options={riderOptions}
                                    value={newDeduction.riderId}
                                    onChange={(value) => setNewDeduction({ ...newDeduction, riderId: value })}
                                    placeholder={ridersLoading ? '로딩 중...' : '선택'}
                                    disabled={ridersLoading}
                                    showAllOption={true}
                                    allOptionLabel="선택"
                                />
                            </td>
                            {/* 공제타입 */}
                            <td>
                                <select
                                    className="form-select"
                                    value={newDeduction.deductionTypeCode}
                                    onChange={(e) => {
                                        const code = e.target.value;
                                        const type = DEDUCTION_TYPE_OPTIONS.find(t => t.code === code);
                                        setNewDeduction({
                                            ...newDeduction,
                                            deductionTypeCode: code,
                                            deductionName: type ? type.name : ''
                                        });
                                    }}
                                >
                                    <option value="">선택</option>
                                    {DEDUCTION_TYPE_OPTIONS.map(type => (
                                        <option key={type.code} value={type.code}>{type.name}</option>
                                    ))}
                                </select>
                            </td>
                            {/* 공제명 */}
                            <td>
                                <input
                                    type="text"
                                    className="filter-input"
                                    value={newDeduction.deductionName}
                                    onChange={(e) => setNewDeduction({ ...newDeduction, deductionName: e.target.value })}
                                    placeholder="공제명"
                                />
                            </td>
                            {/* 공제금액 */}
                            <td>
                                <input
                                    type="text"
                                    className="filter-input"
                                    value={formatCurrency(newDeduction.deductionAmount)}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        setNewDeduction({ ...newDeduction, deductionAmount: value });
                                    }}
                                    placeholder="금액"
                                    style={{ textAlign: 'right' }}
                                />
                            </td>
                            {/* 기간 */}
                            <td>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <DateInput
                                        id="newStartDate"
                                        value={newDeduction.startDate}
                                        onChange={(value) => setNewDeduction({ ...newDeduction, startDate: value })}
                                    />
                                    <span>~</span>
                                    <DateInput
                                        id="newEndDate"
                                        value={newDeduction.endDate}
                                        onChange={(value) => setNewDeduction({ ...newDeduction, endDate: value })}
                                    />
                                </div>
                            </td>
                            {/* 등록 버튼 */}
                            <td className="text-center">
                                <button
                                    className="btn btn-primary btn-register"
                                    onClick={handleAddDeduction}
                                >
                                    <i className="fas fa-plus-circle" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default DeductionRiderToSupplierPage;
